
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsEsigntemplate from 'Models/Security/Acl/VisitorsEsigntemplate';
import UserEsigntemplate from 'Models/Security/Acl/UserEsigntemplate';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IEsigntemplateEntityAttributes extends IModelAttributes {
	name: string;
	lifespanindays: number;
	reminderfrequencyindays: number;

	esigntemplatecontents: Array<
		| Models.EsigntemplatecontentEntity
		| Models.IEsigntemplatecontentEntityAttributes
	>;
	businessEntityId?: string;
	businessEntity?: Models.BusinessEntity | Models.IBusinessEntityAttributes;
	logoId?: string;
	logo?: Models.LogoEntity | Models.ILogoEntityAttributes;
	esignworkflowss: Array<
		| Models.EsignworkflowEntity
		| Models.IEsignworkflowEntityAttributes
	>;

}

@entity('EsigntemplateEntity', 'ESignTemplate')
export default class EsigntemplateEntity extends Model
	implements IEsigntemplateEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsEsigntemplate(),
		new UserEsigntemplate(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<EsigntemplateEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;
	

	
	@Validators.Integer()
	@observable
	@attribute<EsigntemplateEntity, number>()
	@CRUD({
		name: 'LifeSpanInDays',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public lifespanindays: number;
	

	
	@Validators.Integer()
	@observable
	@attribute<EsigntemplateEntity, number>()
	@CRUD({
		name: 'ReminderFrequencyInDays',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public reminderfrequencyindays: number;
	

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ESignTemplateContents',
		displayType: 'reference-multicombobox',
		order: 50,
		referenceTypeFunc: () => Models.EsigntemplatecontentEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'esigntemplatecontents',
			oppositeEntity: () => Models.EsigntemplatecontentEntity,
		}),
		
	})
	public esigntemplatecontents: Models.EsigntemplatecontentEntity[] = [];

	@observable
	@attribute()
	@CRUD({
		
		name: 'Business Entity',
		displayType: 'reference-combobox',
		order: 60,
		referenceTypeFunc: () => Models.BusinessEntity,
		
	})
	public businessEntityId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntity: Models.BusinessEntity;

	@observable
	@attribute()
	@CRUD({
		
		name: 'Logo',
		displayType: 'reference-combobox',
		order: 70,
		referenceTypeFunc: () => Models.LogoEntity,
		
	})
	public logoId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public logo: Models.LogoEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ESignWorkflowss',
		displayType: 'reference-multicombobox',
		order: 80,
		referenceTypeFunc: () => Models.EsignworkflowEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'esignworkflowss',
			oppositeEntity: () => Models.EsignworkflowEntity,
		}),
		
	})
	public esignworkflowss: Models.EsignworkflowEntity[] = [];


	@action public validateField = async (field: string) => {
		switch (field) {
			case 'name': {
				this.setFieldError('name', !this.name, 'Template Name cannot be empty');
				break;
			}
			case 'lifespanindays': {
				this.setFieldError('lifespanindays', !this.lifespanindays, 'Life span cannot be empty');
				if (this.lifespanindays <= 0) {
					this.setFieldError('lifespanindays', true, 'Life span cannot be less than 1');
					break;
				}
				if (this.lifespanindays > 90) {
					this.setFieldError('lifespanindays', true, 'Life span cannot exceed 90 days');
					break;
				}

				if (this.lifespanindays % 1 !== 0) {
					this.setFieldError('lifespanindays', true, 'Life span must be a whole number');
					break;
				}

				this.validateField('reminderfrequencyindays');
				break;
			}
			case 'reminderfrequencyindays': {
				if (this.reminderfrequencyindays < 0) {
					this.setFieldError('reminderfrequencyindays', true, 'Reminder Frequency cannot be empty');
				}

				if (this.lifespanindays <= this.reminderfrequencyindays) {
					this.setFieldError(
						'reminderfrequencyindays',
						true,
						'Reminder Frequency must be less than Life span',
					);
				}

				if (this.reminderfrequencyindays < this.lifespanindays) {
					this.setFieldError('reminderfrequencyindays', false, '');
				}

				break;
			}
		}
	};

	public validateTemplateDetailsFields = async () => {
		const fieldList = [
			'name', 'lifespanindays', 'reminderfrequencyindays',
		];
		const validationPromises = fieldList.map(field => this.validateField(field));
		await Promise.all(validationPromises);
	};

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IEsigntemplateEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IEsigntemplateEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.lifespanindays !== undefined) {
				this.lifespanindays = attributes.lifespanindays;
			}
			if (attributes.reminderfrequencyindays !== undefined) {
				this.reminderfrequencyindays = attributes.reminderfrequencyindays;
			}
			if (attributes.esigntemplatecontents !== undefined && Array.isArray(attributes.esigntemplatecontents)) {
				for (const model of attributes.esigntemplatecontents) {
					if (model instanceof Models.EsigntemplatecontentEntity) {
						this.esigntemplatecontents.push(model);
					} else {
						this.esigntemplatecontents.push(new Models.EsigntemplatecontentEntity(model));
					}
				}
			}
			if (attributes.businessEntityId !== undefined) {
				this.businessEntityId = attributes.businessEntityId;
			}
			if (attributes.businessEntity !== undefined) {
				if (attributes.businessEntity === null) {
					this.businessEntity = attributes.businessEntity;
				} else if (attributes.businessEntity instanceof Models.BusinessEntity) {
					this.businessEntity = attributes.businessEntity;
					this.businessEntityId = attributes.businessEntity.id;
				} else {
					this.businessEntity = new Models.BusinessEntity(attributes.businessEntity);
					this.businessEntityId = this.businessEntity.id;
				}
			}
			if (attributes.logoId !== undefined) {
				this.logoId = attributes.logoId;
			}
			if (attributes.logo !== undefined) {
				if (attributes.logo === null) {
					this.logo = attributes.logo;
				} else if (attributes.logo instanceof Models.LogoEntity) {
					this.logo = attributes.logo;
					this.logoId = attributes.logo.id;
				} else {
					this.logo = new Models.LogoEntity(attributes.logo);
					this.logoId = this.logo.id;
				}
			}
			if (attributes.esignworkflowss !== undefined && Array.isArray(attributes.esignworkflowss)) {
				for (const model of attributes.esignworkflowss) {
					if (model instanceof Models.EsignworkflowEntity) {
						this.esignworkflowss.push(model);
					} else {
						this.esignworkflowss.push(new Models.EsignworkflowEntity(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		esigntemplatecontents {
			${Models.EsigntemplatecontentEntity.getAttributes().join('\n')}
		}
		businessEntity {
			${Models.BusinessEntity.getAttributes().join('\n')}
		}
		logo {
			${Models.LogoEntity.getAttributes().join('\n')}
			${Models.LogoEntity.getFiles().map(f => f.name).join('\n')}
		}
		esignworkflowss {
			${Models.EsignworkflowEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			esigntemplatecontents: {},
			esignworkflowss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(EsigntemplateEntity.prototype, 'created');
CRUD(modifiedAttr)(EsigntemplateEntity.prototype, 'modified');
